/**
 * Placement geometry of the Synth Riders note grid, taken from the desktop (Unity) editor — the
 * editor the milestone measures parity against. One module, so the drawn grid, the hit plane, both
 * clamps and the snap lattice cannot drift apart again (they did: the grid was drawn 0.2 world units
 * above the notes' origin while snapping ignored the offset — CAND-D, CAND-AE).
 *
 * Coordinates are note space (the `.synth` file's own units) unless a name says `World`.
 * World = note × NOTE_TO_WORLD, plus NOTE_Y_OFFSET on Y.
 */
import { GRID_SCALE } from '../../types/beatmap';

/** Note space → web world units. */
export const NOTE_TO_WORLD = GRID_SCALE * 10; // 1.365

/** World Y of note-space y = 0. */
export const NOTE_Y_OFFSET = 1.8;

/** One grid cell at the desktop editor's full resolution (`GridManager.MAX_SEPARATION`). */
export const GRID_CELL = 0.1365;

/**
 * The true centre of every movable object. The desktop editor gives a new wall this position
 * (`Track.cs` `ToggleMovementSectionToChart`: "defaults to actual centre of grid (not 0,0)"), and the
 * game scales positions about the same x. It is not noise to be filtered out.
 */
export const GRID_CENTRE: readonly [number, number] = [0.00199997425, 0.00120002031];

/**
 * Where a note may be placed: 7 cells either side of the centre horizontally and 5 vertically.
 * Left/right/bottom are the desktop editor's `*_GRID_BOUNDARY` constants (`Track.cs`); the top is
 * the top snapped row, 0.6837, by the editor maintainer's decision, rather than the desktop's
 * 0.7596 — half a cell above any row a snapped note can reach.
 */
export const PLACEMENT_LIMITS = { left: -0.9535, right: 0.9575, bottom: -0.6813, top: 0.6837 } as const;

/** The outermost lines are the limits, but GRID_CENTRE ± n cells misses them by float noise
 *  (0.00199997425 - 7 × 0.1365 = -0.95350003); 1e-6 note units is far below a pixel. */
const LIMIT_EPS = 1e-6;

/** The desktop grid: 16 × 12 cells of GRID_CELL, centred on GRID_CENTRE, in world units. */
export const DEFAULT_GRID = {
  gridWidth: 16 * GRID_CELL * NOTE_TO_WORLD,
  gridHeight: 12 * GRID_CELL * NOTE_TO_WORLD,
  gridDivisionsH: 16,
  gridDivisionsV: 12,
  notesGridYOffset: 0,
} as const;

/** Clamp a note-space position to the placement limits. Grid settings never move these. */
export function clampToPlacementLimits(x: number, y: number): [number, number] {
  return [
    Math.max(PLACEMENT_LIMITS.left, Math.min(PLACEMENT_LIMITS.right, x)),
    Math.max(PLACEMENT_LIMITS.bottom, Math.min(PLACEMENT_LIMITS.top, y)),
  ];
}

/** World position of the drawn grid's centre (where its two thick lines cross). */
export function gridCentreWorld(notesGridYOffset = 0): [number, number] {
  return [GRID_CENTRE[0] * NOTE_TO_WORLD, NOTE_Y_OFFSET + GRID_CENTRE[1] * NOTE_TO_WORLD + notesGridYOffset];
}

export type GridLayout = {
  gridWidth: number;
  gridHeight: number;
  gridDivisionsH: number;
  gridDivisionsV: number;
  notesGridYOffset: number;
};

/**
 * World offsets, from the grid's centre, of the drawn lines along one axis: `divisions + 1` lines
 * spanning `size` world units. `GridGuide` draws exactly these, and the snap lattice is built from
 * them, so what is snapped to is what is on screen.
 */
export function gridLineOffsets(size: number, divisions: number): number[] {
  const n = Math.max(1, Math.round(divisions));
  const half = size / 2;
  const cell = size / n;
  return Array.from({ length: n + 1 }, (_, i) => -half + cell * i);
}

/** Note-space coordinates of the drawn lines along one axis, for a grid centred on `centreNote`. */
export function gridLinesNote(size: number, divisions: number, centreNote: number): number[] {
  return gridLineOffsets(size, divisions).map((o) => centreNote + o / NOTE_TO_WORLD);
}

/**
 * Snap a note-space position to the nearest intersection of the grid's lines continued without end in every
 * direction: the lattice of `snapToGridLines`, with no limit on where it reaches. Walls use it, since a wall may
 * lie anywhere (SRVR-0065) and Snap to Grid still moves it in whole cells there.
 */
export function snapToGridLinesUnbounded(x: number, y: number, g: GridLayout): [number, number] {
  const axis = (v: number, size: number, divisions: number, centreNote: number) => {
    const n = Math.max(1, Math.round(divisions));
    const cell = size / n / NOTE_TO_WORLD;
    const origin = centreNote - size / 2 / NOTE_TO_WORLD;   // a drawn line
    return origin + Math.round((v - origin) / cell) * cell;
  };
  return [
    axis(x, g.gridWidth, g.gridDivisionsH, GRID_CENTRE[0]),
    axis(y, g.gridHeight, g.gridDivisionsV, GRID_CENTRE[1] + (g.notesGridYOffset ?? 0) / NOTE_TO_WORLD),
  ];
}

/**
 * Snap a note-space position to the nearest drawn grid intersection that lies inside the placement
 * limits, so a snapped note is always on a line and never clamped off it.
 */
export function snapToGridLines(x: number, y: number, g: GridLayout): [number, number] {
  const xs = gridLinesNote(g.gridWidth, g.gridDivisionsH, GRID_CENTRE[0])
    .filter((v) => v >= PLACEMENT_LIMITS.left - LIMIT_EPS && v <= PLACEMENT_LIMITS.right + LIMIT_EPS);
  const ys = gridLinesNote(g.gridHeight, g.gridDivisionsV, GRID_CENTRE[1] + (g.notesGridYOffset ?? 0) / NOTE_TO_WORLD)
    .filter((v) => v >= PLACEMENT_LIMITS.bottom - LIMIT_EPS && v <= PLACEMENT_LIMITS.top + LIMIT_EPS);
  const nearest = (vals: number[], v: number, fallback: number) =>
    vals.length ? vals.reduce((best, c) => (Math.abs(c - v) < Math.abs(best - v) ? c : best)) : fallback;
  const [cx, cy] = clampToPlacementLimits(x, y);
  return [nearest(xs, x, cx), nearest(ys, y, cy)];
}
